import { z } from 'zod';
import { booleanFilter, datetimeFilter, picklistFilter } from './common/filters';
import { itemFilterSearchSchema } from './itemFilterSearchSchema';

/**
 * Picklist/boolean/datetime item filters are URL-encoded compactly (`teamId=0`,
 * `isUnparented=true`, `plannedStartDateTime=is_2026-07-02`) by `itemFilterUrlCodec`, not as the
 * full `{value,operator}` object. This widens their route search schema to accept the compact raw
 * shapes, while still accepting the full object form so older shared links keep working.
 *
 * Keys are listed explicitly (not derived via `Object.entries`/`Object.fromEntries` over the base
 * schema's shape) so each key keeps its precise literal type when spread into `.extend()` — a
 * dynamically-built override object widens to `Record<string, ZodTypeAny>`, which zod still
 * accepts but infers as `unknown` per key, corrupting this route's search type and, through
 * TanStack Router's global route registry, unrelated routes' type inference app-wide.
 */
const emptyStringToUndefined = (v: unknown) => (v === '' ? undefined : v);

const compactPicklist = z.preprocess(
  emptyStringToUndefined,
  z.union([z.number(), z.string(), picklistFilter]).optional(),
);
const compactBoolean = z.preprocess(
  emptyStringToUndefined,
  z.union([z.boolean(), z.string(), booleanFilter]).optional(),
);
const compactDatetime = z.preprocess(
  emptyStringToUndefined,
  z.union([z.string(), datetimeFilter]).optional(),
);

export const itemFilterUrlSearchSchema = itemFilterSearchSchema.extend({
  effortEstimate: compactPicklist,
  estimationId: compactPicklist,
  itemTypeId: compactPicklist,
  parentId: compactPicklist,
  ancestorId: compactPicklist,
  id: compactPicklist,
  loopId: compactPicklist,
  statusId: compactPicklist,
  teamId: compactPicklist,
  integrationId: compactPicklist,
  objectiveId: compactPicklist,
  isUnparented: compactBoolean,
  isMLImputed: compactBoolean,
  needsTeamOrEstimate: compactBoolean,
  inDoneSubtree: compactBoolean,
  unresolved: compactBoolean,
  hasObjective: compactBoolean,
  createdAt: compactDatetime,
  forecastedFinishDateTime: compactDatetime,
  forecastedStartDateTime: compactDatetime,
  latestFinishDateTime: compactDatetime,
  doneDateTime: compactDatetime,
  plannedStartDateTime: compactDatetime,
  plannedFinishDateTime: compactDatetime,
  deadlineDateTime: compactDatetime,
  earliestStartDateTime: compactDatetime,
  updatedAt: compactDatetime,
});
